import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Alert, 
  Snackbar, 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Badge
} from '@mui/material';
import StageWrapper from './components/StageWrapper';
import EmailGate from './components/EmailGate';
import FlowSelector from './pages/FlowSelector';
import GenerateQuestions from './stages/GenerateQuestions';
import GenerateMFR from './stages/GenerateMFR';
import EnhancedProductDiscovery from './stages/EnhancedProductDiscovery';
import PSCCodeAssignment from './stages/PSCCodeAssignment';
import NAICSCodeMapping from './stages/NAICSCodeMapping';
import VendorDiscovery from './stages/VendorDiscovery';
import SectionBGeneration from './stages/SectionBGeneration';
import { 
  StageConfig,
  MFR,
  Product,
  ProductWithPSC,
  ProductWithCodes,
  Vendor,
  PipelineSession,
} from './types/pipeline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MenuIcon from '@mui/icons-material/Menu';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const stages: StageConfig[] = [
  {
    id: 'questions',
    name: 'Generate Questions',
    description: 'Generate clarifying questions for the commodity requirements',
    nextStage: 'mfr',
  },
  {
    id: 'mfr',
    name: 'Salient Characteristics',
    description: 'Generate Salient Characteristics based on answers',
    prevStage: 'questions',
    nextStage: 'products',
  },
  {
    id: 'products',
    name: 'Product Discovery',
    description: 'Find products that match your requirements',
    prevStage: 'mfr',
    nextStage: 'psc',
  },
  {
    id: 'psc',
    name: 'PSC Code Assignment',
    description: 'Assign Product Service Codes to selected products',
    prevStage: 'products',
    nextStage: 'naics',
  },
  {
    id: 'naics',
    name: 'NAICS Code Mapping',
    description: 'Map PSC codes to NAICS industry classifications',
    prevStage: 'psc',
    nextStage: 'vendors',
  },
  {
    id: 'vendors',
    name: 'Vendor Discovery',
    description: 'Find qualified vendors based on NAICS codes',
    prevStage: 'naics',
    nextStage: 'section-b',
  },
  {
    id: 'section-b',
    name: 'Section B Generation',
    description: 'Generate Section B - Supplies or Services and Prices/Costs',
    prevStage: 'vendors',
  },
];

function App() {
  const [currentStage, setCurrentStage] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'info' | 'error'>('info');
  const [isComplete, setIsComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Email authentication state
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showEmailGate, setShowEmailGate] = useState(true);
  
  // Flow management state
  const [flowId, setFlowId] = useState<string | null>(null);
  const [showFlowSelector, setShowFlowSelector] = useState(true);
  
  // Pipeline data state
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [mfr, setMfr] = useState<MFR | null>(null);
  const [initialCommodityDescription, setInitialCommodityDescription] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [productsWithPSC, setProductsWithPSC] = useState<ProductWithPSC[]>([]);
  const [productsWithCodes, setProductsWithCodes] = useState<ProductWithCodes[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [sectionBDocument, setSectionBDocument] = useState<string>('');

  // Stage completion handlers
  const handleQuestionsComplete = (questionData: any[], answerData: Record<string, string>, commodityDesc: string) => {
    console.log('[App] handleQuestionsComplete called');
    // Try to derive commodity description from first question or existing input if missing
    setQuestions(questionData);
    setAnswers(answerData);
    // Set commodity directly from Stage 1 input for clean architecture
    if (commodityDesc && commodityDesc.trim()) {
      console.log('[App] Setting initialCommodityDescription from Stage 1 input:', commodityDesc);
      setInitialCommodityDescription(commodityDesc);
    } else {
      console.log('[App] Empty commodityDesc passed; keeping existing initialCommodityDescription:', initialCommodityDescription);
    }
    setCurrentStage(1);
    showNotificationMessage('Questions answered! Moving to Salient Characteristics.', 'success');
    saveSession();
  };

  const handleMFRComplete = (mfrData: MFR) => {
    setMfr(mfrData);
    setCurrentStage(2);
    showNotificationMessage('Salient Characteristics generated! Moving to product discovery.', 'success');
    saveSession();
  };

  const handleProductsComplete = (selectedProducts: Product[]) => {
    setProducts(selectedProducts);
    setCurrentStage(3);
    showNotificationMessage(`${selectedProducts.length} products selected. Proceeding to PSC assignment.`, 'success');
    saveSession();
  };

  const handlePSCComplete = (productsWithPSCData: ProductWithPSC[]) => {
    setProductsWithPSC(productsWithPSCData);
    setCurrentStage(4);
    showNotificationMessage('PSC codes assigned. Moving to NAICS mapping.', 'success');
    saveSession();
  };

  const handleNAICSComplete = (productsWithCodesData: ProductWithCodes[]) => {
    setProductsWithCodes(productsWithCodesData);
    setCurrentStage(5);
    showNotificationMessage('NAICS codes mapped. Proceeding to vendor discovery.', 'success');
    saveSession();
  };

  const handleVendorsComplete = (productsWithVendors: any[]) => {
    // Store the products with vendors (new structure from golden script)
    setVendors(productsWithVendors as any);
    const totalVendors = productsWithVendors.reduce((sum, p) => sum + (p.vendors?.length || 0), 0);
    showNotificationMessage(`${totalVendors} vendors identified. Proceeding to Section B generation.`, 'success');
    setCurrentStage(6); // Move to Stage 7 (Section B)
    saveSession();
  };

  const handleSectionBComplete = (finalDocument: string) => {
    // Store the final Section B document
    setSectionBDocument(finalDocument);
    setIsComplete(true);
    showNotificationMessage('Section B document finalized! Procurement package complete.', 'success');
    saveSession();
  };

  const showNotificationMessage = (
    message: string, 
    type: 'success' | 'info' | 'error' = 'info'
  ) => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
  };

  const handleNext = () => {
    // Navigation handled by individual stage completion handlers
    showNotificationMessage('Please complete the current stage to proceed', 'info');
  };

  const handleBack = () => {
    if (currentStage > 0) {
      setCurrentStage(currentStage - 1);
      showNotificationMessage(`Returned to ${stages[currentStage - 1].name}`, 'info');
    }
  };

  const handleStageSelect = (stageIndex: number) => {
    // Allow navigation if user has completed up to that stage or it's a previous stage
    const hasData = 
      (stageIndex === 0) ||
      (stageIndex === 1 && questions.length > 0) ||
      (stageIndex === 2 && mfr) ||
      (stageIndex === 3 && products.length > 0) ||
      (stageIndex === 4 && productsWithPSC.length > 0) ||
      (stageIndex === 5 && productsWithCodes.length > 0) ||
      (stageIndex === 6 && vendors.length > 0);
    
    if (hasData || stageIndex < currentStage) {
      setCurrentStage(stageIndex);
      showNotificationMessage(`Navigated to ${stages[stageIndex].name}`, 'info');
    } else {
      showNotificationMessage('Please complete the previous stages first', 'info');
    }
  };

  // Legacy manual save removed; session persists automatically where needed

  const saveSession = () => {
    const session: PipelineSession = {
      id: 'session-1',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      currentStage,
      stages: {
        questions: {
          commodityDescription: initialCommodityDescription,
          questions,
          answers: Object.entries(answers).map(([questionId, answer]) => ({
            questionId,
            answer,
            question: questions.find(q => q.id === questionId)?.question || '',
            category: questions.find(q => q.id === questionId)?.category || '',
          })),
        },
        mfr: {
          questions,
          answers: Object.entries(answers).map(([questionId, answer]) => ({
            questionId,
            answer,
            question: questions.find(q => q.id === questionId)?.question || '',
            category: questions.find(q => q.id === questionId)?.category || '',
          })),
          mfr,
        },
        products: {
          mfr,
          products,
          selectedProducts: products.map(p => p.id),
        },
        psc: {
          products: productsWithPSC,
        },
        naics: {
          products: productsWithCodes,
        },
        vendors: {
          vendors,
          selectedVendors: vendors.map(v => v.id),
        },
        sectionB: {
          document: sectionBDocument,
          finalOutput: sectionBDocument,
        },
      },
    };
    localStorage.setItem('farchat-session', JSON.stringify(session));
  };

  const loadSession = () => {
    const saved = localStorage.getItem('farchat-session');
    if (saved) {
      try {
        const session: PipelineSession = JSON.parse(saved);
        if (session.stages.questions?.questions) setQuestions(session.stages.questions.questions);
        if (session.stages.questions?.answers) {
          // Convert QA[] to Record<string, string>
          const answersObj: Record<string, string> = {};
          session.stages.questions.answers.forEach((qa: any) => {
            if (qa.questionId) {
              answersObj[qa.questionId] = qa.answer || '';
            }
          });
          setAnswers(answersObj);
        }
        if (session.stages.mfr?.mfr) setMfr(session.stages.mfr.mfr);
        if (session.stages.products?.products) setProducts(session.stages.products.products);
        if (session.stages.psc?.products) setProductsWithPSC(session.stages.psc.products);
        if (session.stages.naics?.products) setProductsWithCodes(session.stages.naics.products);
        if (session.stages.vendors?.vendors) setVendors(session.stages.vendors.vendors);
        if (session.stages.sectionB?.document) setSectionBDocument(session.stages.sectionB.document);
        // Ensure currentStage is within valid bounds
        setCurrentStage(Math.min(session.currentStage, stages.length - 1));
        showNotificationMessage('Previous session restored', 'info');
      } catch (e) {
        console.error('Failed to load session:', e);
      }
    }
  };

  const handleRestart = () => {
    if (window.confirm('Are you sure you want to start over? All progress will be lost.')) {
      setQuestions([]);
      setAnswers({});
      setMfr(null);
      setProducts([]);
      setProductsWithPSC([]);
      setProductsWithCodes([]);
      setVendors([]);
      setSectionBDocument('');
      setCurrentStage(0);
      setIsComplete(false);
      localStorage.removeItem('farchat-session');
      showNotificationMessage('Pipeline reset. Starting fresh.', 'info');
    }
  };

  // Email authentication handler
  const handleEmailSubmit = (email: string) => {
    setUserEmail(email);
    setShowEmailGate(false);
    // Store email for API calls
    window.userEmail = email;
  };

  // Flow management handlers
  const handleSelectFlow = async (selectedFlowId: string) => {
    setFlowId(selectedFlowId);
    setShowFlowSelector(false);
    
    // Load flow data and set appropriate stage
    try {
      const userEmail = localStorage.getItem('userEmail') || window.userEmail || '';
      const headers: Record<string, string> = {};
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }
      const response = await fetch(`${API_BASE_URL}/api/flows/${selectedFlowId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        const metadata = data.metadata;
        
        // Load stage data if available
        if (metadata.completedStages && metadata.completedStages.length > 0) {
          // Load actual stage data from backend - prefer UI format
          for (const stageNum of metadata.completedStages) {
            // Try UI format first
            let stageResponse = await fetch(`${API_BASE_URL}/api/flows/${selectedFlowId}/stage/${stageNum}/ui`, { headers });
            let isUIFormat = true;
            
            if (!stageResponse.ok) {
              // Fallback to raw format
              stageResponse = await fetch(`${API_BASE_URL}/api/flows/${selectedFlowId}/stage/${stageNum}`, { headers });
              isUIFormat = false;
            }
            
            if (stageResponse.ok) {
              const stageData = await stageResponse.json();
              console.log(`Loading stage ${stageNum} data (${isUIFormat ? 'UI format' : 'raw format'}):`, stageData);
              
              // Populate state based on stage output
              if (stageData.output) {
                switch (stageNum) {
                  case 1: // Generate Questions
                    // Load the original commodity description from input
                    if (stageData.input) {
                      const commodityDesc = stageData.input.commodityDescription || stageData.input.commodity_description;
                      if (commodityDesc) {
                        setInitialCommodityDescription(commodityDesc);
                      }
                    }
                    
                    if (stageData.output.questions) {
                      console.log(`Setting ${stageData.output.questions.length} questions`);
                      setQuestions(stageData.output.questions);
                    }
                    // Load answers from stage 1 if available
                    if (stageData.output.answers) {
                      console.log('Setting answers from stage 1:', stageData.output.answers);
                      setAnswers(stageData.output.answers);
                    }
                    break;
                  
                  case 2: // Generate MFR
                    if (stageData.output.minimum_requirements) {
                      // Parse requirements into proper structure
                      const requirementsObj = {
                        functional: [],
                        performance: [],
                        environmental: [],
                        integration: [],
                        other: []
                      };
                      
                      const mfrData = {
                        id: `mfr-${Date.now()}`,
                        commodityDescription: stageData.output.original_spec || '',
                        requirements: requirementsObj,  // Use empty object structure
                        generatedAt: new Date().toISOString(),
                        questionsAnswered: stageData.output.answers || [],
                        rawText: stageData.output.minimum_requirements,  // Keep the text in rawText
                      };
                      console.log('Setting MFR:', mfrData);
                      setMfr(mfrData);
                    }
                    if (stageData.output.answers) {
                      setAnswers(stageData.output.answers);
                    }
                    break;
                  
                  case 3: // Products
                    if (stageData.output.products) {
                      console.log(`Setting ${stageData.output.products.length} products`);
                      setProducts(stageData.output.products);
                    }
                    break;
                  
                  case 4: // PSC Codes
                    if (stageData.output.productsWithPSC || stageData.output.products_with_psc) {
                      const pscData = stageData.output.productsWithPSC || stageData.output.products_with_psc;
                      console.log(`Setting ${pscData.length} products with PSC`);
                      setProductsWithPSC(pscData);
                    }
                    break;
                  
                  case 5: // NAICS Codes
                    if (stageData.output.productsWithNAICS || stageData.output.products_with_naics) {
                      const naicsData = stageData.output.productsWithNAICS || stageData.output.products_with_naics;
                      console.log(`Setting ${naicsData.length} products with NAICS`);
                      setProductsWithCodes(naicsData);
                    }
                    break;
                  
                  case 6: // Vendors
                    if (stageData.output.productsWithVendors || stageData.output.products_with_vendors) {
                      console.log(`Setting vendors data`);
                      setVendors(stageData.output.productsWithVendors || stageData.output.products_with_vendors);
                    }
                    break;
                  
                  case 7: // Section B
                    // Stage 7 doesn't need state, it's the final output
                    console.log('Stage 7 (Section B) output exists but doesn\'t need state');
                    break;
                }
              } else {
                console.warn(`Stage ${stageNum} has no output data`);
              }
            } else {
              console.error(`Failed to load stage ${stageNum}: HTTP ${stageResponse.status}`);
            }
          }
          
          // Convert from 1-indexed backend stage to 0-indexed React stage
          // If metadata.currentStage is 1, React stage should be 0
          // If metadata.currentStage is 7, React stage should be 6
          const backendStage = metadata.currentStage || 1;
          const stageIndex = Math.min(backendStage - 1, stages.length - 1);
          setCurrentStage(Math.max(0, stageIndex));
          showNotificationMessage(`Loaded flow: ${metadata.name}`, 'info');
        }
      }
    } catch (err) {
      console.error('Error loading flow:', err);
      showNotificationMessage('Failed to load flow data', 'error');
    }
  };

  const handleCreateNewFlow = (newFlowId: string) => {
    setFlowId(newFlowId);
    setShowFlowSelector(false);
    setCurrentStage(0);
    showNotificationMessage('New workflow created. Starting with requirements.', 'success');
  };

  // Create default workflow on mount if not showing selector
  const createDefaultWorkflow = async () => {
    try {
      const userEmail = localStorage.getItem('userEmail') || window.userEmail || '';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }
      const response = await fetch(`${API_BASE_URL}/api/flows`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: `Workflow ${new Date().toISOString().split('T')[0]}`,
          description: 'Auto-created workflow',
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlowId(data.flow_id);
        console.log('Created default workflow:', data.flow_id);
      }
    } catch (err) {
      console.error('Error creating default workflow:', err);
    }
  };

  // Removed JSON export of full package for non-technical audiences

  // Load saved session on mount (unless auto-clear is enabled)
  useEffect(() => {
    // Check if we should skip the flow selector
    const skipFlowSelector = process.env.REACT_APP_SKIP_FLOW_SELECTOR === 'true';
    
    if (skipFlowSelector) {
      // Create default workflow and skip selector
      setShowFlowSelector(false);
      createDefaultWorkflow();
    }
    
    if (process.env.REACT_APP_AUTO_CLEAR_SESSION === 'true') {
      localStorage.removeItem('farchat-session');
      console.log('Session auto-cleared based on environment setting');
    } else {
      loadSession();
    }
  }, []);

  const canProceed = () => {
    // Each stage manages its own progression
    return false;
  };

  const renderStage = () => {
    if (isComplete) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Procurement Process Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Successfully identified {vendors.length} qualified vendors for your requirements.
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            {/* Removed JSON export of full package */}
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleRestart}
              size="large"
            >
              Start New Process
            </Button>
          </Box>
        </Paper>
      );
    }

    switch (currentStage) {
      case 0:
        return (
          <GenerateQuestions
            onComplete={handleQuestionsComplete}
            initialDescription={initialCommodityDescription}
            flowId={flowId}
            initialAnswers={answers}
          />
        );
      case 1:
        console.log('[App] Render GenerateMFR with commodity:', initialCommodityDescription);
        return questions.length > 0 ? (
          <GenerateMFR
            commodityDescription={initialCommodityDescription}
            questions={questions}
            onComplete={handleMFRComplete}
            flowId={flowId}
            initialAnswers={answers}
          />
        ) : (
          <Alert severity="error">No questions available. Please complete question generation first.</Alert>
        );
      case 2:
        return mfr ? (
          <EnhancedProductDiscovery
            mfr={mfr}
            onComplete={handleProductsComplete}
            flowId={flowId}
          />
        ) : (
          <Alert severity="error">No MFR data available. Please complete MFR generation first.</Alert>
        );
      case 3:
        return products.length > 0 ? (
          <PSCCodeAssignment
            products={products}
            onComplete={handlePSCComplete}
            flowId={flowId}
          />
        ) : (
          <Alert severity="error">No products selected. Please complete product discovery first.</Alert>
        );
      case 4:
        return productsWithPSC.length > 0 ? (
          <NAICSCodeMapping
            products={productsWithPSC}
            onComplete={handleNAICSComplete}
            flowId={flowId}
          />
        ) : (
          <Alert severity="error">No PSC codes assigned. Please complete PSC assignment first.</Alert>
        );
      case 5:
        return productsWithCodes.length > 0 ? (
          <VendorDiscovery
            products={productsWithCodes}
            onComplete={handleVendorsComplete}
            flowId={flowId}
          />
        ) : (
          <Alert severity="error">No NAICS codes mapped. Please complete NAICS mapping first.</Alert>
        );
      case 6:
        return vendors.length > 0 ? (
          <SectionBGeneration
            productsWithVendors={vendors as any}
            onComplete={handleSectionBComplete}
            flowId={flowId}
          />
        ) : (
          <Alert severity="error">No vendor data available. Please complete vendor discovery first.</Alert>
        );
      default:
        return null;
    }
  };

  // Get icon for each stage
  const getStageIcon = (stageId: string) => {
    switch (stageId) {
      case 'questions': return <AssignmentIcon />;
      case 'mfr': return <AssignmentIcon />;
      case 'products': return <SearchIcon />;
      case 'psc': return <CategoryIcon />;
      case 'naics': return <BusinessIcon />;
      case 'vendors': return <StoreIcon />;
      case 'section-b': return <AssignmentIcon />;
      default: return <AssignmentIcon />;
    }
  };

  // Check if stage has data
  const stageHasData = (index: number) => {
    switch (index) {
      case 0: return true;
      case 1: return questions.length > 0;
      case 2: return !!mfr;
      case 3: return products.length > 0;
      case 4: return productsWithPSC.length > 0;
      case 5: return productsWithCodes.length > 0;
      case 6: return vendors.length > 0;
      default: return false;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Show EmailGate first if no email */}
      {showEmailGate && !userEmail ? (
        <EmailGate 
          open={true} 
          onEmailSubmit={handleEmailSubmit} 
        />
      ) : /* Show FlowSelector if no flow is selected */
      showFlowSelector ? (
        <FlowSelector
          onSelectFlow={handleSelectFlow}
          onCreateNew={handleCreateNewFlow}
        />
      ) : (
        <>
      {/* Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Pipeline Navigation
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              if (window.confirm('Return to flow selector? Current progress will be saved.')) {
                setShowFlowSelector(true);
                setDrawerOpen(false);
              }
            }}
            sx={{ mb: 2 }}
          >
            Change Workflow
          </Button>
          <List>
            {stages.map((stage, index) => {
              const hasData = stageHasData(index);
              const isActive = index === currentStage;
              const isCompleted = index < currentStage || (index === 0 && questions.length > 0) || (index === 1 && mfr);
              
              return (
                <ListItem key={stage.id} disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      handleStageSelect(index);
                      setDrawerOpen(false);
                    }}
                    disabled={!hasData && index > currentStage}
                  >
                    <ListItemIcon>
                      <Badge 
                        color={isCompleted ? "success" : "default"} 
                        variant="dot"
                        invisible={!isCompleted}
                      >
                        {getStageIcon(stage.id)}
                      </Badge>
                    </ListItemIcon>
                    <ListItemText 
                      primary={stage.name}
                      secondary={
                        isActive ? 'Current' : 
                        isCompleted ? 'Completed' : 
                        hasData ? 'In Progress' : 
                        'Not Started'
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <Divider sx={{ my: 2 }} />
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={() => {
              handleRestart();
              setDrawerOpen(false);
            }}
          >
            Start Over
          </Button>
        </Box>
      </Drawer>

      {/* Menu Button - Always visible */}
      <Box sx={{ position: 'fixed', top: 20, left: 20, zIndex: 1300 }}>
        <IconButton
          color="primary"
          onClick={() => setDrawerOpen(true)}
          sx={{ 
            bgcolor: 'background.paper', 
            boxShadow: 2,
            '&:hover': { boxShadow: 4 }
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {!isComplete ? (
        <StageWrapper
          config={stages[currentStage]}
          currentStage={currentStage}
          totalStages={stages.length}
          stages={stages.map(s => s.name)}
          onNext={handleNext}
          onBack={handleBack}
          onStageSelect={handleStageSelect}
          canProceed={canProceed()}
        >
          {renderStage()}
        </StageWrapper>
      ) : (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {renderStage()}
        </Container>
      )}
      
      <Snackbar
        open={showNotification}
        autoHideDuration={4000}
        onClose={() => setShowNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowNotification(false)} 
          severity={notificationType}
          sx={{ width: '100%' }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
      </>
      )}
    </ThemeProvider>
  );
}

export default App;
